import { useEffect, useState } from 'react';

/**
 * Microphone level for a single audio track, measured locally with WebAudio.
 *
 * Deliberately not LiveKit's `audioLevel`: that only arrives with the SFU's
 * active-speaker updates, so it is coarse, it only carries a value for whoever
 * is currently loudest, and it says nothing at all about the local microphone —
 * which is the one people most want a meter for.
 *
 * One AudioContext and one animation frame serve every meter on the page. All
 * of them tick together, so a twelve-person call costs one loop, not twelve.
 */

/** Lit segments in the meter. The level is quantised to this before it reaches
    React, so a tile only re-renders when the display actually changes — a
    continuous 0..1 value would re-render every participant on every frame. */
export const METER_SEGMENTS = 7;

/* The window mapped onto the meter, in dBFS. Speech sits around -25 dB, so the
   top of the scale is well short of 0: a meter that only fills when you shout
   tells you nothing. Anything under the floor reads as silence, which keeps
   room tone from lighting the first segment permanently. */
const FLOOR_DB = -60;
const TOP_DB = -6;

/* Fast attack, slow release — the ballistics of a real meter. Following the
   instantaneous level instead makes the segments flicker on every syllable. */
const RELEASE = 0.86;

interface Meter {
  /** Held only so the source node's stream cannot be collected under it. */
  stream: MediaStream;
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  /** Explicitly backed by an ArrayBuffer, not a SharedArrayBuffer, which is
      what `getFloatTimeDomainData` insists on. */
  buf: Float32Array<ArrayBuffer>;
  level: number;
  segments: number;
  listeners: Set<(segments: number) => void>;
}

let ctx: AudioContext | null = null;
let frame = 0;
const meters = new Map<MediaStreamTrack, Meter>();

function tick(): void {
  frame = requestAnimationFrame(tick);
  for (const meter of meters.values()) {
    const buf = meter.buf;
    meter.analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const sample = buf[i] ?? 0;
      sum += sample * sample;
    }
    const rms = Math.sqrt(sum / buf.length);
    // The floor on rms keeps log10 away from -Infinity on a digitally silent
    // track, which a muted microphone produces exactly.
    const db = 20 * Math.log10(Math.max(rms, 1e-8));
    const value = Math.max(0, Math.min(1, (db - FLOOR_DB) / (TOP_DB - FLOOR_DB)));
    meter.level = value > meter.level ? value : meter.level * RELEASE + value * (1 - RELEASE);

    const segments = Math.round(meter.level * METER_SEGMENTS);
    if (segments === meter.segments) continue;
    meter.segments = segments;
    for (const listener of meter.listeners) listener(segments);
  }
}

function subscribe(track: MediaStreamTrack, listener: (segments: number) => void): () => void {
  const AudioCtx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) return () => {};

  if (!ctx) ctx = new AudioCtx();
  // A context built before any user gesture starts suspended, and a suspended
  // analyser reads silence forever.
  void ctx.resume().catch(() => {});

  let meter = meters.get(track);
  if (!meter) {
    const stream = new MediaStream([track]);
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    /* Connected to nothing downstream. This graph is measurement only —
       routing it to the destination would play every remote voice a second
       time, out of step with the tile's own <video> element. Remote tracks are
       already being pumped by that element, which is what keeps this analyser
       fed. */
    source.connect(analyser);
    meter = {
      stream,
      source,
      analyser,
      buf: new Float32Array(analyser.fftSize),
      level: 0,
      segments: 0,
      listeners: new Set(),
    };
    meters.set(track, meter);
    if (!frame) frame = requestAnimationFrame(tick);
  }

  meter.listeners.add(listener);
  listener(meter.segments);

  return () => {
    const existing = meters.get(track);
    if (!existing) return;
    existing.listeners.delete(listener);
    if (existing.listeners.size > 0) return;

    existing.source.disconnect();
    meters.delete(track);
    if (meters.size > 0) return;
    // Nothing left to measure: stop the loop and give the audio hardware back
    // rather than leaving a context running for the rest of the session.
    cancelAnimationFrame(frame);
    frame = 0;
    const closing = ctx;
    ctx = null;
    void closing?.close().catch(() => {});
  };
}

/** Lit segments, 0…METER_SEGMENTS. Returns 0 while `active` is false, so a
    muted microphone shows a dead meter instead of the last level it saw. */
export function useAudioLevel(track: MediaStreamTrack | null | undefined, active = true): number {
  const [segments, setSegments] = useState(0);

  useEffect(() => {
    if (!track || !active) {
      setSegments(0);
      return;
    }
    return subscribe(track, setSegments);
  }, [track, active]);

  return segments;
}
